import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { Modal, Backdrop, Fade, CircularProgress } from "@material-ui/core";
import { Formik, Form, Field, FieldArray } from "formik";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import CaseService from "../../../../services/CaseService"; // ← adjust path
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../../helpers/functions";
import AWSService from "../../../../services/AWSService";

const formatLabel = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const getSectionColor = (key) => {
  const k = key.toLowerCase();
  if (k.includes("conciliation")) return "#8B008B";
  if (k.includes("neutral")) return COLORS.WARNING;
  if (k.includes("claimant")) return COLORS.BTN_GREEN;
  if (k.includes("respondent")) return COLORS.HYPERLINK;
  if (k.includes("document")) return COLORS.TRANSCRIPT_GRAY;
  return COLORS.COLOR_DARK;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function deepMerge(base, override) {
  if (!override || typeof override !== "object") return base;
  const result = { ...base };
  Object.keys(override).forEach((k) => {
    const ov = override[k];
    const bv = base[k];
    if (
      ov !== null &&
      ov !== undefined &&
      typeof ov === "object" &&
      !Array.isArray(ov) &&
      bv !== null &&
      bv !== undefined &&
      typeof bv === "object" &&
      !Array.isArray(bv)
    ) {
      result[k] = deepMerge(bv, ov);
    } else if (ov !== null && ov !== undefined) {
      result[k] = ov;
    }
  });
  return result;
}
/*

const renderFields = (obj, path, edit) => {
  if (!obj || typeof obj !== "object") return null;

  return Object.entries(obj).map(([key, value]) => {
    const fieldPath = path ? `${path}.${key}` : key;

    if (value === null || value === undefined) {
      return (
        <FieldItem key={fieldPath}>
          <FieldLabel>{formatLabel(key)}</FieldLabel>
          {edit ? (
            <Field name={fieldPath}>
              {({ field }) => (
                <FieldInput
                  {...field}
                  value={field.value ?? ""}
                  placeholder="—"
                />
              )}
            </Field>
          ) : (
            <NullValue>—</NullValue>
          )}
        </FieldItem>
      );
    }

    if (Array.isArray(value)) {
      return (
        <div key={fieldPath} style={{ gridColumn: "1 / -1" }}>
          <Section>
            <SectionHeader>
              <SectionDot color={getSectionColor(key)} />
              <SectionTitle>{formatLabel(key)}</SectionTitle>
              <SectionCount>{value.length} items</SectionCount>
            </SectionHeader>
            <SectionBody>
              <FieldArray name={fieldPath}>
                {({ remove }) =>
                  value.map((item, i) => (
                    <ArrayItem key={i}>
                      <ArrayItemHeader>
                        <ArrayItemLabel>Entry {i + 1}</ArrayItemLabel>
                        {edit && (
                          <RemoveBtn type="button" onClick={() => remove(i)}>
                            <DeleteOutlineIcon />
                            Remove
                          </RemoveBtn>
                        )}
                      </ArrayItemHeader>
                      <FieldGrid>
                        {renderFields(item, `${fieldPath}[${i}]`, edit)}
                      </FieldGrid>
                    </ArrayItem>
                  ))
                }
              </FieldArray>
            </SectionBody>
          </Section>
        </div>
      );
    }

    if (typeof value === "object") {
      return (
        <div key={fieldPath} style={{ gridColumn: "1 / -1" }}>
          <Section>
            <SectionHeader>
              <SectionDot color={getSectionColor(key)} />
              <SectionTitle>{formatLabel(key)}</SectionTitle>
            </SectionHeader>
            <SectionBody>
              <FieldGrid>{renderFields(value, fieldPath, edit)}</FieldGrid>
            </SectionBody>
          </Section>
        </div>
      );
    }

    return (
      <FieldItem key={fieldPath}>
        <FieldLabel>{formatLabel(key)}</FieldLabel>
        <Field name={fieldPath}>
          {({ field }) => (
            <FieldInput
              {...field}
              value={field.value ?? ""}
              disabled={!edit}
              type={typeof value === "number" ? "number" : "text"}
            />
          )}
        </Field>
      </FieldItem>
    );
  });
};

*/

const renderFields = (obj, path, edit) => {
  if (!obj || typeof obj !== "object") return null;

  return Object.entries(obj).map(([key, value]) => {
    const fieldPath = path ? `${path}.${key}` : key;

    if (value === null || value === undefined) {
      return (
        <FieldItem key={fieldPath}>
          <FieldLabel>{formatLabel(key)}</FieldLabel>
          {edit ? (
            <Field name={fieldPath}>
              {({ field }) => {
                if (key === "conOutcome") {
                  return (
                    <SelectInput as="select" {...field}>
                      {CON_OUTCOME_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </SelectInput>
                  );
                }
                if (key === "isFeePaidByMP") {
                  return (
                    <SelectInput
                      as="select"
                      value=""
                      onChange={(e) =>
                        field.onChange({
                          target: {
                            name: field.name,
                            value:
                              e.target.value === "true"
                                ? true
                                : e.target.value === "false"
                                  ? false
                                  : null,
                          },
                        })
                      }
                    >
                      {BOOLEAN_OPTIONS.map((opt) => (
                        <option
                          key={opt.value.toString()}
                          value={opt.value.toString()}
                        >
                          {opt.label}
                        </option>
                      ))}
                    </SelectInput>
                  );
                }

                return (
                  <FieldInput
                    {...field}
                    value={field.value ?? ""}
                    placeholder="—"
                  />
                );
              }}
            </Field>
          ) : (
            <NullValue>—</NullValue>
          )}
        </FieldItem>
      );
    }
    if (Array.isArray(value)) {
      return (
        <div key={fieldPath} style={{ gridColumn: "1 / -1" }}>
          <Section>
            <SectionHeader>
              <SectionDot color={getSectionColor(key)} />
              <SectionTitle>{formatLabel(key)}</SectionTitle>
              <SectionCount>{value.length} items</SectionCount>
            </SectionHeader>
            <SectionBody>
              <FieldArray name={fieldPath}>
                {({ remove }) =>
                  value.map((item, i) => (
                    <ArrayItem key={i}>
                      <ArrayItemHeader>
                        <ArrayItemLabel>Entry {i + 1}</ArrayItemLabel>
                        {edit && (
                          <RemoveBtn type="button" onClick={() => remove(i)}>
                            <DeleteOutlineIcon />
                            Remove
                          </RemoveBtn>
                        )}
                      </ArrayItemHeader>
                      <FieldGrid>
                        {renderFields(item, `${fieldPath}[${i}]`, edit)}
                      </FieldGrid>
                    </ArrayItem>
                  ))
                }
              </FieldArray>
            </SectionBody>
          </Section>
        </div>
      );
    }
    if (typeof value === "object") {
      return (
        <div key={fieldPath} style={{ gridColumn: "1 / -1" }}>
          <Section>
            <SectionHeader>
              <SectionDot color={getSectionColor(key)} />
              <SectionTitle>{formatLabel(key)}</SectionTitle>
            </SectionHeader>
            <SectionBody>
              <FieldGrid>{renderFields(value, fieldPath, edit)}</FieldGrid>
            </SectionBody>
          </Section>
        </div>
      );
    }

    return (
      <FieldItem key={fieldPath}>
        <FieldLabel>{formatLabel(key)}</FieldLabel>
        <Field name={fieldPath}>
          {({ field, form }) => {
            if (key === "conOutcome") {
              return (
                <SelectInput
                  as="select"
                  {...field}
                  disabled={!edit}
                  value={field.value ?? ""}
                >
                  {CON_OUTCOME_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </SelectInput>
              );
            }
            if (key === "isFeePaidByMP") {
              return (
                <SelectInput
                  as="select"
                  disabled={!edit}
                  value={
                    field.value === true
                      ? "true"
                      : field.value === false
                        ? "false"
                        : ""
                  }
                  onChange={(e) =>
                    form.setFieldValue(
                      fieldPath,
                      e.target.value === "true"
                        ? true
                        : e.target.value === "false"
                          ? false
                          : null,
                    )
                  }
                >
                  {BOOLEAN_OPTIONS.map((opt) => (
                    <option
                      key={opt.value.toString()}
                      value={opt.value.toString()}
                    >
                      {opt.label}
                    </option>
                  ))}
                </SelectInput>
              );
            }
            return (
              <FieldInput
                {...field}
                value={field.value ?? ""}
                disabled={!edit}
                type={typeof value === "number" ? "number" : "text"}
              />
            );
          }}
        </Field>
      </FieldItem>
    );
  });
};

const renderTopFields = (data, edit) => {
  const flat = Object.entries(data).filter(
    ([, v]) => v === null || v === undefined || typeof v !== "object",
  );
  if (!flat.length) return null;

  return (
    <Section>
      <SectionHeader>
        <SectionDot color={COLORS.COLOR_DARK} />
        <SectionTitle>Dispute Overview</SectionTitle>
      </SectionHeader>
      <SectionBody>
        <FieldGrid>
          {flat.map(([key, value]) => (
            <FieldItem key={key}>
              <FieldLabel>{formatLabel(key)}</FieldLabel>
              <Field name={key}>
                {({ field, form }) => {
                  if (key === "conOutcome") {
                    return (
                      <SelectInput
                        // variant="outlined"
                        as="select"
                        {...field}
                        disabled={!edit}
                        value={field.value ?? ""}
                      >
                        {CON_OUTCOME_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </SelectInput>
                    );
                  }
                  if (key === "isFeePaidByMP") {
                    return (
                      <SelectInput
                        // variant="outlined"
                        as="select"
                        disabled={!edit}
                        value={
                          field.value === true
                            ? "true"
                            : field.value === false
                              ? "false"
                              : ""
                        }
                        onChange={(e) =>
                          form.setFieldValue(
                            key,
                            e.target.value === "true"
                              ? true
                              : e.target.value === "false"
                                ? false
                                : null,
                          )
                        }
                      >
                        {BOOLEAN_OPTIONS.map((opt) => (
                          <option
                            key={opt.value.toString()}
                            value={opt.value.toString()}
                          >
                            {opt.label}
                          </option>
                        ))}
                      </SelectInput>
                    );
                  }
                  if (value === null || value === undefined) {
                    return edit ? (
                      <FieldInput
                        {...field}
                        value={field.value ?? ""}
                        placeholder="—"
                      />
                    ) : (
                      <NullValue>—</NullValue>
                    );
                  }

                  return (
                    <FieldInput
                      {...field}
                      value={field.value ?? ""}
                      disabled={!edit}
                    />
                  );
                }}
              </Field>
            </FieldItem>
          ))}
        </FieldGrid>
      </SectionBody>
    </Section>
  );
};

/**
 * Props
 *   disputeModal    { state: bool, disputeData: obj | null }
 *   setDisputeModal setter
 *   id              case / dispute id  (passed to service calls)
 *   caseDetails     { resolutionKind: string, ... }
 *   onSaveSuccess   optional callback after successful PUT
 */

const CON_OUTCOME_OPTIONS = [
  "Successful",
  "Unsuccessful",
  "Matter kept on hold",
  "Matter withdrawn",
  "Matter Settled",
  "Ex-parte Order",
  "In progress",
];

const BOOLEAN_OPTIONS = [
  { label: "True", value: true },
  { label: "False", value: false },
];

const DisputeModal = ({
  disputeModal,
  setDisputeModal,
  id,
  caseDetails,
  onSaveSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [fetchLoading, setFetchLoading] = useState(false);
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef(null);
  const formikRef = useRef(null);

  useEffect(() => {
    if (disputeModal?.state && !disputeModal?.disputeData) {
      fetchReportDetails();
    }
    if (!disputeModal?.state) {
      setEdit(false);
      setUploadedFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disputeModal?.state]);

  const fetchReportDetails = async () => {
    try {
      setFetchLoading(true);
      const res = await CaseService.fetchReportDetails(
        id,
        caseDetails?.resolutionKind,
      );
      if (res?.success) {
        setDisputeModal((prev) => ({ ...prev, disputeData: res?.data }));
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setFetchLoading(false);
    }
  };

  const addFiles = (files) => {
    const VALID = ["application/pdf"];
    const valid = files.filter((f) => VALID.includes(f.type));
    const skipped = files.length - valid.length;
    if (skipped > 0) {
      enqueueSnackbar(`${skipped} file(s) skipped – unsupported type`, {
        variant: "warning",
      });
    }
    setUploadedFiles((prev) => [
      ...prev,
      ...valid.map((f) => ({ file: f, name: f.name, size: f.size })),
    ]);
  };

  const handleFileChange = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeFile = (index) =>
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

  const handleAutoFill = async () => {
    if (!uploadedFiles.length) {
      enqueueSnackbar("Please upload at least one file first", {
        variant: "warning",
      });
      return;
    }
    try {
      setAutoFillLoading(true);

      const uploadedUrls = await Promise.all(
        uploadedFiles.map(async ({ file }) => {
          const payload = {
            key: file.name,
          };
          const s3Response = await AWSService.getS3URL(payload);
          if (s3Response?.url) {
            return await AWSService.uploadToS3(s3Response.url, file);
          }
          return null;
        }),
      );

      const validUrls = uploadedUrls.filter(Boolean);
      const payload = {
        file: validUrls || [],
        resolutionKind: caseDetails?.resolutionKind || "",
      };
      const res = await CaseService.autoExtractData({ reportData: payload });
      if (formikRef.current) {
        const merged = deepMerge(formikRef.current.values, res);
        formikRef.current.setValues(merged);
      }
      enqueueSnackbar("Fields auto-filled successfully", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setAutoFillLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setSaveLoading(true);
      console.log(values, "values");
      const res = await CaseService.updateReportDetails(
        caseDetails?.id,
        caseDetails?.resolutionKind,
        {
          values,
        },
      );
      if (res?.message) {
        enqueueSnackbar(res?.message, { variant: "success" });
        setEdit(false);
        setDisputeModal((prev) => ({
          ...prev,
          disputeData: res?.data ?? prev.disputeData,
        }));
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setSaveLoading(false);
      setDisputeModal({ state: false });
    }
  };

  const handleClose = () => {
    setDisputeModal({ state: false, disputeData: null });
    setEdit(false);
    setUploadedFiles([]);
  };

  const disputeData = disputeModal?.disputeData?.dispute || {};
  const hasData = Object.keys(disputeData).length > 0;
  const topObjects = Object.entries(disputeData).filter(
    ([, v]) => v !== null && v !== undefined && typeof v === "object",
  );

  return (
    <Modal
      open={disputeModal?.state || false}
      onClose={handleClose}
      BackdropComponent={Backdrop}
    >
      <Fade in={disputeModal?.state || false} timeout={220}>
        <Wrapper>
          <Card>
            <Header>
              <HeaderLeft>
                <HeaderBadge />
                <div>
                  <HeaderTitle>Dispute Details</HeaderTitle>
                  <HeaderSubtitle>
                    {disputeData.disputeId || "—"} ·{" "}
                    {disputeData.disputetype || "—"}
                  </HeaderSubtitle>
                </div>
              </HeaderLeft>
              <IconBtn onClick={handleClose} title="Close">
                <CloseIcon style={{ fontSize: 18 }} />
              </IconBtn>
            </Header>
            {(disputeData.disputetype || disputeData.role) && (
              <StatusBar>
                {disputeData.disputetype && (
                  <StatusChip>{disputeData.disputetype}</StatusChip>
                )}
                {disputeData.role && (
                  <StatusItem>
                    Role&nbsp;<span>{disputeData.role}</span>
                  </StatusItem>
                )}
                {disputeData.userName && (
                  <StatusItem>
                    User&nbsp;<span>{disputeData.userName}</span>
                  </StatusItem>
                )}
              </StatusBar>
            )}
            <Body>
              {fetchLoading && (
                <CenterBox>
                  <SpinningIcon />
                  <span>Loading dispute details…</span>
                </CenterBox>
              )}

              {!fetchLoading && (
                <>
                  <UploadSection>
                    <UploadHeader>
                      <SectionDot color={COLORS.HYPERLINK} />
                      <UploadTitle>Upload Documents</UploadTitle>
                    </UploadHeader>
                    <UploadBody>
                      <DropZone
                        $active={dragActive}
                        htmlFor="dispute-file-input"
                        onDragEnter={handleDrag}
                        onDragOver={handleDrag}
                        onDragLeave={handleDrag}
                        onDrop={handleDrop}
                      >
                        <DropZoneIcon />
                        <DropZoneText>
                          {dragActive
                            ? "Drop files here"
                            : "Click or drag files to upload"}
                        </DropZoneText>
                        <DropZoneHint>
                          PDF · Multiple files allowed
                        </DropZoneHint>
                        <HiddenInput
                          id="dispute-file-input"
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept=".pdf"
                          onChange={handleFileChange}
                        />
                      </DropZone>
                      {uploadedFiles.length > 0 && (
                        <FileList>
                          {uploadedFiles.map((f, i) => (
                            <FileChip key={i}>
                              <FileChipIcon />
                              <FileChipName title={f.name}>
                                {f.name}
                              </FileChipName>
                              <FileChipSize>
                                {formatFileSize(f.size)}
                              </FileChipSize>
                              <RemoveFileBtn
                                type="button"
                                onClick={() => removeFile(i)}
                                title="Remove"
                              >
                                <CloseIcon style={{ fontSize: 14 }} />
                              </RemoveFileBtn>
                            </FileChip>
                          ))}
                        </FileList>
                      )}
                      <UploadActions>
                        <AutoFillBtn
                          type="button"
                          onClick={handleAutoFill}
                          disabled={!uploadedFiles.length || autoFillLoading}
                        >
                          {autoFillLoading ? (
                            <>
                              <CircularProgress
                                size={14}
                                style={{ color: COLORS.COLOR_DARK }}
                              />
                              Auto-filling…
                            </>
                          ) : (
                            <>
                              <AutorenewIcon style={{ fontSize: 16 }} />
                              Auto Fill from Document
                            </>
                          )}
                        </AutoFillBtn>
                      </UploadActions>
                    </UploadBody>
                  </UploadSection>
                  {hasData && (
                    <Formik
                      innerRef={formikRef}
                      initialValues={disputeData}
                      enableReinitialize
                      onSubmit={handleSave}
                    >
                      {({ values }) => (
                        <Form>
                          <Toolbar>
                            {!edit ? (
                              <EditBtn
                                type="button"
                                onClick={() => setEdit(true)}
                              >
                                <EditIcon style={{ fontSize: 16 }} />
                                Edit Details
                              </EditBtn>
                            ) : (
                              <>
                                <CancelBtn
                                  type="button"
                                  onClick={() => setEdit(false)}
                                  disabled={saveLoading}
                                >
                                  Cancel
                                </CancelBtn>
                                <SaveBtn type="submit" disabled={saveLoading}>
                                  {saveLoading ? (
                                    <>
                                      <CircularProgress
                                        size={14}
                                        style={{ color: "#fff" }}
                                      />
                                      Saving…
                                    </>
                                  ) : (
                                    <>
                                      <SaveIcon style={{ fontSize: 16 }} />
                                      Save Changes
                                    </>
                                  )}
                                </SaveBtn>
                              </>
                            )}
                          </Toolbar>
                          {renderTopFields(values, edit)}
                          {topObjects.map(([key, value]) => {
                            if (Array.isArray(value)) {
                              return (
                                <Section key={key}>
                                  <SectionHeader>
                                    <SectionDot color={getSectionColor(key)} />
                                    <SectionTitle>
                                      {formatLabel(key)}
                                    </SectionTitle>
                                    <SectionCount>
                                      {(values[key] || []).length} items
                                    </SectionCount>
                                  </SectionHeader>
                                  <SectionBody>
                                    <FieldArray name={key}>
                                      {({ remove }) =>
                                        (values[key] || []).map((item, i) => (
                                          <ArrayItem key={i}>
                                            <ArrayItemHeader>
                                              <ArrayItemLabel>
                                                Entry {i + 1}
                                              </ArrayItemLabel>
                                              {edit && (
                                                <RemoveBtn
                                                  type="button"
                                                  onClick={() => remove(i)}
                                                >
                                                  <DeleteOutlineIcon />
                                                  Remove
                                                </RemoveBtn>
                                              )}
                                            </ArrayItemHeader>
                                            <FieldGrid>
                                              {renderFields(
                                                item,
                                                `${key}[${i}]`,
                                                edit,
                                              )}
                                            </FieldGrid>
                                          </ArrayItem>
                                        ))
                                      }
                                    </FieldArray>
                                  </SectionBody>
                                </Section>
                              );
                            }
                            return (
                              <Section key={key}>
                                <SectionHeader>
                                  <SectionDot color={getSectionColor(key)} />
                                  <SectionTitle>
                                    {formatLabel(key)}
                                  </SectionTitle>
                                </SectionHeader>
                                <SectionBody>
                                  <FieldGrid>
                                    {renderFields(values[key], key, edit)}
                                  </FieldGrid>
                                </SectionBody>
                              </Section>
                            );
                          })}
                        </Form>
                      )}
                    </Formik>
                  )}
                </>
              )}
            </Body>
          </Card>
        </Wrapper>
      </Fade>
    </Modal>
  );
};

export default DisputeModal;

const slideUp = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const Wrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(100% - 32px);
  max-width: 900px;
  transform: translate(-50%, -50%);
  animation: ${slideUp} 0.28s cubic-bezier(0.22, 1, 0.36, 1) both;
  outline: none;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  box-shadow:
    0 32px 80px rgba(41, 52, 97, 0.18),
    0 0 0 1px rgba(41, 52, 97, 0.06);
`;

const Header = styled.div`
  padding: 18px 24px;
  background: ${COLORS.COLOR_DARK};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderBadge = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${COLORS.HIGHLIGHT_COLOR};
  box-shadow: 0 0 0 3px rgba(105, 227, 194, 0.25);
`;

const HeaderTitle = styled.h2`
  margin: 0 0 2px;
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 16px;
  color: #ffffff;
  letter-spacing: 0.01em;
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

const IconBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.18s,
    border-color 0.18s,
    transform 0.12s;
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
  &:active {
    transform: scale(0.96);
  }
`;

const StatusBar = styled.div`
  padding: 9px 24px;
  background: ${COLORS.COLOR_DARK}ee;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-wrap: wrap;
`;

const StatusChip = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(105, 227, 194, 0.15);
  color: ${COLORS.HIGHLIGHT_COLOR};
  border: 1px solid rgba(105, 227, 194, 0.3);
`;

const StatusItem = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  span {
    color: rgba(255, 255, 255, 0.8);
    font-family: ${theme.fonts.primaryFontSemiBold};
  }
`;

const Body = styled.div`
  padding: 20px 24px 24px;
  overflow-y: auto;
  flex: 1;
  background: #f7f9ff;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${COLORS.BORDER_GREY};
    border-radius: 4px;
  }
`;

const CenterBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: ${COLORS.INPUT_LABEL};
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
`;

const SpinningIcon = styled(AutorenewIcon)`
  animation: ${spin} 1s linear infinite;
  font-size: 32px !important;
  color: ${COLORS.BTN_GREEN};
`;

// ─────────────────────────────────────────────
// Upload Zone
// ─────────────────────────────────────────────

const UploadSection = styled.div`
  margin-bottom: 16px;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid ${COLORS.BORDER_GREY};
  overflow: hidden;
  animation: ${fadeIn} 0.22s ease both;
`;

const UploadHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 18px;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
  background: #fafbff;
`;

const UploadTitle = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
`;

const UploadBody = styled.div`
  padding: 16px 18px;
`;

const DropZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid
    ${(p) => (p.$active ? COLORS.BTN_GREEN : COLORS.BORDER_GREY)};
  border-radius: 12px;
  padding: 28px 20px;
  background: ${(p) =>
    p.$active ? "rgba(0,131,140,0.04)" : COLORS.TABLE_GREY_CELL};
  cursor: pointer;
  transition:
    border-color 0.18s,
    background 0.18s;
  &:hover {
    border-color: ${COLORS.BTN_GREEN};
    background: rgba(0, 131, 140, 0.04);
  }
`;

const DropZoneIcon = styled(CloudUploadIcon)`
  font-size: 30px !important;
  color: ${COLORS.BTN_GREEN};
  opacity: 0.75;
`;

const DropZoneText = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
`;

const DropZoneHint = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11px;
  color: ${COLORS.INPUT_LABEL};
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: ${COLORS.TABLE_GREY_CELL};
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 8px;
`;

const FileChipIcon = styled(InsertDriveFileIcon)`
  font-size: 16px !important;
  color: ${COLORS.BTN_GREEN};
  flex-shrink: 0;
`;

const FileChipName = styled.div`
  flex: 1;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: ${COLORS.COLOR_DARK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileChipSize = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11px;
  color: ${COLORS.INPUT_LABEL};
  flex-shrink: 0;
`;

const RemoveFileBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${COLORS.INPUT_LABEL};
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: 4px;
  transition: color 0.15s;
  &:hover {
    color: ${COLORS.REMOVE_RED};
  }
`;

const UploadActions = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

// ─────────────────────────────────────────────
// Toolbar
// ─────────────────────────────────────────────

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 16px;
  padding: 11px 16px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid ${COLORS.BORDER_GREY};
`;

const BtnBase = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 18px;
  border-radius: 9px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.18s;
  border: 1.5px solid transparent;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
  svg {
    font-size: 16px;
  }
`;

const EditBtn = styled(BtnBase)`
  background: ${COLORS.COLOR_DARK};
  color: #ffffff;
  &:hover:not(:disabled) {
    background: #1e274a;
  }
`;

const SaveBtn = styled(BtnBase)`
  background: ${COLORS.BTN_GREEN};
  color: #ffffff;
  &:hover:not(:disabled) {
    background: #006a72;
  }
`;

const CancelBtn = styled(BtnBase)`
  background: transparent;
  color: ${COLORS.INPUT_LABEL};
  border-color: ${COLORS.BORDER_GREY};
  &:hover:not(:disabled) {
    background: ${COLORS.TABLE_GREY_CELL};
    color: ${COLORS.COLOR_DARK};
  }
`;

const AutoFillBtn = styled(BtnBase)`
  background: ${COLORS.LIGHT_BLUE};
  color: ${COLORS.COLOR_DARK};
  border-color: ${COLORS.BORDER_GREY};
  &:hover:not(:disabled) {
    background: #e8eeff;
    border-color: ${COLORS.COLOR_DARK};
  }
`;

// ─────────────────────────────────────────────
// Sections
// ─────────────────────────────────────────────

const Section = styled.div`
  margin-bottom: 16px;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid ${COLORS.BORDER_GREY};
  overflow: hidden;
  animation: ${fadeIn} 0.22s ease both;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 18px 11px;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
  background: #fafbff;
`;

const SectionDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => p.color || COLORS.BTN_GREEN};
  flex-shrink: 0;
`;

const SectionTitle = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
`;

const SectionCount = styled.span`
  margin-left: auto;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11px;
  color: ${COLORS.INPUT_LABEL};
  background: ${COLORS.TABLE_GREY_CELL};
  padding: 2px 8px;
  border-radius: 10px;
`;

const SectionBody = styled.div`
  padding: 16px 18px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
`;

const FieldItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldLabel = styled.label`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  color: ${COLORS.INPUT_LABEL};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const SelectInput = styled.select`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  background: ${(p) => (p.disabled ? COLORS.TABLE_GREY_CELL : "#ffffff")};
  border: 1.5px solid
    ${(p) => (p.disabled ? "transparent" : COLORS.INPUT_BORDER)};
  border-radius: 8px;
  padding: 8px 36px 8px 12px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  cursor: ${(p) => (p.disabled ? "default" : "pointer")};

  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  background-image: url("data:image/svg+xml;utf8,<svg fill='%23333' height='20' viewBox='0 0 20 20' width='20' xmlns='http://www.w3.org/2000/svg'><path d='M5 7l5 5 5-5z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;

  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${COLORS.BTN_GREEN};
    background-color: rgba(0, 131, 140, 0.02);
  }

  &:focus {
    border-color: ${COLORS.BTN_GREEN};
    box-shadow: 0 0 0 3px rgba(0, 131, 140, 0.1);
  }

  &:disabled {
    color: ${COLORS.COLOR_DARK};
  }
`;

const FieldInput = styled.input`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  background: ${(p) => (p.disabled ? COLORS.TABLE_GREY_CELL : "#ffffff")};
  border: 1.5px solid
    ${(p) => (p.disabled ? "transparent" : COLORS.INPUT_BORDER)};
  border-radius: 8px;
  padding: 8px 12px;
  outline: none;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  width: 100%;
  box-sizing: border-box;
  &:focus {
    border-color: ${COLORS.BTN_GREEN};
    box-shadow: 0 0 0 3px rgba(0, 131, 140, 0.1);
  }
  &:disabled {
    cursor: default;
    color: ${COLORS.COLOR_DARK};
  }
`;

const NullValue = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: ${COLORS.INPUT_GRAY};
  background: ${COLORS.TABLE_GREY_CELL};
  border-radius: 8px;
  padding: 8px 12px;
  border: 1.5px solid transparent;
`;

const ArrayItem = styled.div`
  position: relative;
  padding: 14px;
  background: ${COLORS.TABLE_GREY_CELL};
  border-radius: 10px;
  margin-bottom: 10px;
  border: 1px solid ${COLORS.BORDER_GREY};
  &:last-child {
    margin-bottom: 0;
  }
`;

const ArrayItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ArrayItemLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  color: ${COLORS.INPUT_LABEL};
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 7px;
  border: 1.5px solid rgba(255, 60, 60, 0.25);
  background: rgba(255, 60, 60, 0.06);
  color: ${COLORS.REMOVE_RED};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: rgba(255, 60, 60, 0.12);
    border-color: rgba(255, 60, 60, 0.4);
    transform: scale(1.02);
  }
  &:active {
    transform: scale(0.96);
  }
  svg {
    font-size: 15px;
  }
`;
